<template>
	<main class="mypage signin">
		<div class="signin_form">
			<h6>회원가입</h6>
			<div class="c_fieldArea">
				<FieldLayout title="이름" :error="errors.member_name">
					<InputText
						v-model="member_name"
						:show-error-icon="errors.member_name"
						:placeholder="INPUT_NAME"
						:class="{ error: errors.member_name }"
						:disabled="route.query.name ? true : false"
					/>
				</FieldLayout>
				<FieldLayout
					v-if="joinPath === 'normal'"
					title="아이디"
					:success="member_id_valid ? MESSAGE_AVAILABLE_ID : ''"
					:error="errors.member_id || errors.member_id_valid"
					@input="
						(e: Event) => {
							const validateValue = (e.target as HTMLInputElement).value.replace(/[^a-zA-Z0-9]/g, '');
							member_id = validateValue;
							if (member_id_valid) member_id_valid = false;
						}
					"
				>
					<InputText
						v-model="member_id"
						:placeholder="ERROR_INVALID_ID"
						:show-error-icon="errors.member_id"
						:class="{ error: errors.member_id }"
						@blur="
							() => {
								if (member_id && !member_id_valid) setFieldError('member_id', MESSAGE_ID_DUPLICATE_CHECK_COMPLETED);
							}
						"
					/>
					<button class="c_button primary-fill radius-x-small" :disabled="!member_id" @click="onClickId">
						중복확인
					</button>
				</FieldLayout>
				<FieldLayout
					title="닉네임"
					:success="member_nickname_valid ? MESSAGE_AVAILABLE_NICKNAME : ''"
					:error="errors.member_nickname || errors.member_nickname_valid"
					@input="
						() => {
							if (member_nickname_valid) member_nickname_valid = false;
						}
					"
				>
					<InputText
						v-model="member_nickname"
						:placeholder="ERROR_INVALID_INPUT"
						:show-error-icon="errors.member_nickname"
						:class="{ error: errors.member_nickname }"
						@blur="
							() => {
								if (member_nickname && !member_nickname_valid)
									setFieldError('member_nickname', MESSAGE_NICKNAME_DUPLICATE_CHECK_COMPLETED);
							}
						"
					/>
					<button class="c_button primary-fill radius-x-small" :disabled="!member_nickname" @click="onClickNickname">
						중복확인
					</button>
				</FieldLayout>
				<FieldLayout
					v-if="joinPath === 'normal'"
					title="비밀번호 (대문자, 특수문자 금지)"
					:error="errors.member_password"
				>
					<InputPasswordLock
						v-model="member_password"
						:placeholder="PLACEHOLDER_PASSWORD_RULE"
						:class="{ error: errors.member_password }"
						show-toggle
						:show-error-icon="errors.member_password"
					/>
				</FieldLayout>
				<FieldLayout v-if="joinPath === 'normal'" title="비밀번호 확인" :error="errors.member_password_re">
					<InputPasswordLock
						v-model="member_password_re"
						:placeholder="PLACEHOLDER_PASSWORD_RE"
						:class="{ error: errors.member_password_re }"
						show-toggle
						:show-error-icon="errors.member_password_re"
					/>
				</FieldLayout>
				<FieldPhoneValidate
					v-model:phoneNum="member_cell"
					v-model:isPhoneValid="member_cell_valid"
					required
					phone-type="SignUp"
					:phoneNum_error="errors.member_cell"
					:isPhoneValid_error="errors.member_cell_valid"
					type="join"
					@update:phone-num="(value) => chagePhoneNumber(value)"
					@update:is-phone-valid="(value) => changeCellValid(value)"
					@update:is-phone-code="(value) => changePhoneCode(value)"
					@update:set-field-error="(value) => onChangePhoneError(value)"
				/>
				<!-- 추천인 코드 주석처리 START 260722-->
				<!--
				<FieldLayout
					title="추천인 코드"
					:error="errors.recommender || errors.recommender_valid"
					@input="
						() => {
							if (recommender_valid) {
								recommender_valid = false;
							}
							if (recommender.length <= 0) {
								recommender_valid = true;
							}
						}
					"
				>
					<InputText
						v-model="recommender"
						:class="{ error: errors.recommender }"
						:show-error-icon="errors.recommender"
					/>
					<button class="c_button primary-fill radius-x-small" :disabled="!recommender" @click="onClickRecommender">
						확인
					</button>
				</FieldLayout>
				-->
				<!-- 추천인 코드 주석처리 END -->
				<div class="receive">
					<span>서비스 알림 수신 설정</span>
					<SliderCheckbox value="" :model-value="isReceive" @update:model-value="onChangeSlider" />
				</div>
				<button
					class="c_button primary-fill register"
					:disabled="
						joinPath === 'normal'
							? !member_name ||
								!member_id ||
								!member_id_valid ||
								!member_nickname ||
								!member_nickname_valid ||
								!member_password ||
								!member_password_re ||
								!member_cell ||
								!member_cell_valid
							: !member_name ||
								!member_nickname ||
								!member_nickname_valid ||
								!member_cell ||
								!member_cell_valid
					"
					@click="onClickSignUp"
				>
					회원가입
				</button>
			</div>
		</div>
	</main>
</template>

<script setup lang="ts">
// core
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
// validation
import { useForm } from 'vee-validate';
import * as yup from 'yup';

// api
import {
	SignupDataType,
	useCheckDuplicateNicknameApi,
	useCheckIdDuplicateApi,
	// useCheckRecommendCodeApi, // 추천인 코드 주석처리 260722
	useSignupApi,
} from '@/api/auth';
// component
import SliderCheckbox from '@/components/atom/checkbox/SliderCheckbox.vue';
import InputPasswordLock from '@/components/atom/input/InputPasswordLock.vue';
import InputText from '@/components/atom/input/InputText.vue';
import FieldLayout from '@/components/form/FieldLayout.vue';
import FieldPhoneValidate from '@/components/form/FieldPhoneValidate.vue';
// constant
import { isRegex, nameRegex, nicknameRegex, passRegex, phoneRegex } from '@/constants/regex';
import {
	ALREADY_ID,
	ALREADY_NICKNAME,
	// ERROR_ALREADY_RECOMMEND, // 추천인 코드 주석처리 260722
	ERROR_INVALID_FORMAT,
	ERROR_INVALID_ID_FORMAT,
	ERROR_INVALID_INPUT,
	ERROR_INVALID_INPUT_FORMAT,
	ERROR_PASSWORDS_DO_NOT_MATCH,
	ERROR_REQUIRED_FIELD,
	ERROR_SIGNUP_FAILED,
	ERROR_INVALID_ID,
	ERROR_PHONE_VERIFICATION_REQUIRED,
	INPUT_NAME,
	MESSAGE_AVAILABLE_ID,
	MESSAGE_AVAILABLE_NICKNAME,
	MESSAGE_ID_DUPLICATE_CHECK_COMPLETED,
	MESSAGE_NICKNAME_DUPLICATE_CHECK_COMPLETED,
	MESSAGE_PASSWORD_REQUIREMENTS,
	MESSAGE_PHONE_VERIFICATION_COMPLETED,
	// MESSAGE_REFERRER_DUPLICATE_CHECK_COMPLETED, // 추천인 코드 주석처리 260722
	MESSAGE_TRY_AGAIN_LATER,
	MESSAGE_VERIFY_PHONE_NUMBER,
	PLACEHOLDER_PASSWORD_RE,
	PLACEHOLDER_PASSWORD_RULE,
	COMPANY_TITLE,
} from '@/constants/title';
import { useAuthStore } from '@/stores/auth';
// store
import { usePopupStore } from '@/stores/popup';
import { useTokenStore } from '@/stores/token';

// -----variable-----
const route = useRoute();
const router = useRouter();
const popupStore = usePopupStore();
const tokenStore = useTokenStore();
const authStore = useAuthStore();

const joinPath = route.query.joinPath;
const isNormalPath = route.query.joinPath === 'normal';

const isReceive = ref(true); //서비스 알림 수신설정

// -----validation-----
const validationSchema = yup.object({
	member_name: yup.string().required(ERROR_INVALID_FORMAT).matches(nameRegex, ERROR_INVALID_FORMAT),
	...(isNormalPath
		? {
				member_id: yup.string().required(ERROR_REQUIRED_FIELD).matches(isRegex, ERROR_INVALID_ID_FORMAT),
				member_id_valid: yup.boolean().isTrue(MESSAGE_ID_DUPLICATE_CHECK_COMPLETED),
				member_password: yup
					.string()
					.required(MESSAGE_PASSWORD_REQUIREMENTS)
					.matches(passRegex, MESSAGE_PASSWORD_REQUIREMENTS),
				member_password_re: yup.string().oneOf([yup.ref('member_password')], ERROR_PASSWORDS_DO_NOT_MATCH),
			}
		: {}),
	member_nickname: yup.string().required(ERROR_INVALID_INPUT_FORMAT).matches(nicknameRegex, ERROR_INVALID_INPUT_FORMAT),
	member_nickname_valid: yup.boolean().isTrue(MESSAGE_NICKNAME_DUPLICATE_CHECK_COMPLETED),
	member_cell: yup.string().required(ERROR_REQUIRED_FIELD).min(13, '').matches(phoneRegex, MESSAGE_VERIFY_PHONE_NUMBER),
	member_cell_code: yup.string().required(ERROR_PHONE_VERIFICATION_REQUIRED).min(6, ERROR_PHONE_VERIFICATION_REQUIRED),
	member_cell_valid: yup.boolean().isTrue(MESSAGE_PHONE_VERIFICATION_COMPLETED),
	// 추천인 코드 주석처리 START 260722
	// recommender: yup.string(),
	// recommender_valid: yup.boolean().when('recommender', (recommender) => {
	// 	return recommender && recommender.length > 0
	// 		? yup.boolean().oneOf([true], MESSAGE_REFERRER_DUPLICATE_CHECK_COMPLETED)
	// 		: yup.boolean().notRequired(); // 유효성 검사를 하지 않도록 설정
	// }),
	// 추천인 코드 주석처리 END
});
const { errors, defineField, setFieldError, validate } = useForm({
	validationSchema,
	initialValues: {
		member_name: route.query.name ? String(route.query.name) : '',
		member_id: '',
		member_id_valid: false,
		member_nickname: '',
		member_nickname_valid: false,
		member_password: '',
		member_password_re: '',
		member_cell: '',
		member_cell_valid: false,
		member_cell_code: '',
		// 추천인 코드 주석처리 START 260722
		// recommender: '',
		// recommender_valid: true,
		// 추천인 코드 주석처리 END
	},
});

const [member_id] = defineField('member_id'); //회원아이디
const [member_id_valid] = defineField('member_id_valid'); // 아이디 중복확인 여부
const [member_name] = defineField('member_name'); //회원이름
const [member_nickname] = defineField('member_nickname'); //회원닉네임
const [member_nickname_valid] = defineField('member_nickname_valid'); //닉네임 중복확인 여부
const [member_cell] = defineField('member_cell'); //회원 휴대폰 번호
const [member_cell_valid] = defineField('member_cell_valid'); // 휴대폰번호 인증 여부
const [member_cell_code] = defineField('member_cell_code'); // 휴대폰 인증번호
const [member_password] = defineField('member_password'); //회원 비밀번호
const [member_password_re] = defineField('member_password_re'); //회원 비밀번호 재확인
// 추천인 코드 주석처리 START 260722
// const [recommender] = defineField('recommender'); // 추천인
// const [recommender_valid] = defineField('recommender_valid'); //추천인 중복확인 여부
// 추천인 코드 주석처리 END

// -----api-----
const { mutateAsync: checkDuplicateNickname } = useCheckDuplicateNicknameApi();
const { mutateAsync: checkIdDuplicate } = useCheckIdDuplicateApi();
// const { mutateAsync: checkRecommendCode } = useCheckRecommendCodeApi(); // 추천인 코드 주석처리 260722
const { mutateAsync: signup, isPending: isSignupPending } = useSignupApi();

// -----function-----
// 아이디 중복체크
const onClickId = async () => {
	const res = await checkIdDuplicate({ member_id: member_id.value });
	if (res.data.success) {
		member_id_valid.value = true;
	} else {
		setFieldError('member_id', ALREADY_ID);
	}
};
// 닉네임 중복 체크
const onClickNickname = async () => {
	const res = await checkDuplicateNickname({
		member_nickname: member_nickname.value,
	});
	if (res.data.success) {
		member_nickname_valid.value = true;
	} else if (!res.data.success) {
		setFieldError('member_nickname', ALREADY_NICKNAME);
	}
};
// 추천인 코드 주석처리 260722
// // 추천인 중복 체크
// const onClickRecommender = async () => {
// 	const res = await checkRecommendCode({
// 		recommend_code: recommender.value,
// 	});
// 	if (res.data.success) {
// 		recommender_valid.value = true;
// 	} else if (!res.data.success) {
// 		setFieldError('recommender', ERROR_ALREADY_RECOMMEND);
// 	}
// };
// 서비스 알림 수신 설정
const onChangeSlider = (boolean: boolean) => {
	isReceive.value = boolean;
};
// 휴대폰 번호 인증 여부
const changeCellValid = (v: boolean) => {
	member_cell_valid.value = v;
};
// 휴대폰 번호 변경
const chagePhoneNumber = (v: string) => {
	member_cell.value = v;
	member_cell_valid.value = false;
	member_cell_code.value = '';
};
// 휴대폰 인증번호 변경
const changePhoneCode = (v: string) => {
	member_cell_code.value = v;
};
// 회원가입
const onClickSignUp = async () => {
	const { valid } = await validate();

	if (!valid) {
		return;
	}
	if (!member_cell_valid.value || !member_cell_code.value) {
		setFieldError('member_cell_valid', ERROR_PHONE_VERIFICATION_REQUIRED);
		return;
	}
	if (isSignupPending.value) {
		return;
	}
	const params: SignupDataType = {
		member_name: member_name.value,
		member_nickname: member_nickname.value,
		member_contact: member_cell.value,
		member_join_path: joinPath as string,
		member_push_agree: String(isReceive.value),
		member_id: member_id.value,
		member_password: member_password.value,
		identification_no: member_cell_code.value,
		// recommend_code: recommender.value, // 추천인 코드 주석처리 260722
	};
	if (joinPath !== 'normal') {
		params.access_token = tokenStore.getAccessTokenForSNSSignUp;
		params.member_join_path = (authStore.getJoinPath || String(joinPath)) ?? '';
		if (!params.access_token) {
			popupStore.openPopup({
				content: COMPANY_TITLE,
				subtext: ERROR_SIGNUP_FAILED,
			});
			return;
		}
	}
	const res = await signup(params);
	if (res.data.success) {
		router.replace({
			name: 'auth/signup-complete',
			query: typeof route.query.redirect === 'string' ? { redirect: route.query.redirect } : {},
		});
	} else {
		const errorCode = res.data?.error?.code;
		if (errorCode === '79') {
			popupStore.openPopup({
				content: COMPANY_TITLE,
				subtext: ERROR_PHONE_VERIFICATION_REQUIRED,
			});
			member_cell_valid.value = false;
			return;
		}
		if (errorCode === '60') {
			popupStore.openPopup({
				content: COMPANY_TITLE,
				subtext: '이미 가입이 완료된 계정입니다. 로그인해 주세요.',
			});
			return;
		}
		popupStore.openPopup({
			content: ERROR_SIGNUP_FAILED,
			subtext: MESSAGE_TRY_AGAIN_LATER,
		});
	}
};
// 휴대폰 에러 값 설정
const onChangePhoneError = (value: string) => {
	setFieldError('member_cell', value);
};
</script>

<style lang="scss" scoped>
.signin {
	max-width: 40rem;
	padding-bottom: var(--bottomMenu-height);
	.signin_form {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 4rem;
		.register {
			height: 4.8rem;
			font-size: var(--font-s-regular);
			font-weight: var(--font-w-medium);
		}
		.c_fieldArea {
			.phone_number {
				display: flex;
				flex-direction: column;
				gap: 1rem;
			}
		}
	}
}
.FieldLayout .field button {
	width: 7.7rem;
}
</style>
